import React, { useCallback, useEffect, useState } from 'react';
import { Badge, Button, Modal, Spinner } from 'react-bootstrap';
import {
  FaArrowLeft, FaAward, FaCertificate, FaCheckCircle, FaClipboardCheck,
  FaClock, FaCrown, FaDownload, FaExternalLinkAlt, FaFileAlt, FaFlagCheckered,
  FaGraduationCap, FaInfoCircle, FaLaptop, FaLock, FaMapMarkerAlt, FaPlay,
  FaPoll, FaRoute, FaVideo
} from 'react-icons/fa';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../../../api/apiClient';
import { getAssetUrl } from '../../../utils/apiBase';
import { generateModulesUrl } from '../../../utils/urlSecurity';

const STEP_ICONS = {
  course: FaGraduationCap,
  assessment: FaClipboardCheck,
  vilt: FaLaptop,
  survey: FaPoll,
  certificate: FaCertificate,
  info: FaInfoCircle
};

const DEFAULT_DURATIONS = {
  course: 20,
  assessment: 15,
  vilt: 60,
  survey: 10,
  certificate: 5,
  info: 5
};

const getStepDuration = (step) => {
  if (step.step_type === 'course') {
    const totalMinutes = (step.courseModules || []).reduce(
      (total, courseModule) => total + Number(courseModule.module?.duration || 0),
      0
    );
    if (totalMinutes > 0) return `${totalMinutes} min`;
  }

  if (step.step_type === 'assessment') {
    const minutes = Number(step.assessmentLink?.assessment?.duration_minutes || 0);
    if (minutes > 0) return `${minutes} min`;
  }

  return `${DEFAULT_DURATIONS[step.step_type] || 10} min`;
};

const buildRoadPath = (stepCount) => {
  if (stepCount === 0) return '';

  const points = [{ x: 500, y: 35 }];
  for (let index = 0; index < stepCount; index += 1) {
    points.push({
      x: index % 2 === 0 ? 260 : 740,
      y: 210 + (index * 220)
    });
  }
  points.push({ x: 500, y: (stepCount * 220) + 165 });

  return points.reduce((path, point, index) => {
    if (index === 0) return `M ${point.x} ${point.y}`;
    const previous = points[index - 1];
    const middleY = (previous.y + point.y) / 2;
    return `${path} C ${previous.x} ${middleY}, ${point.x} ${middleY}, ${point.x} ${point.y}`;
  }, '');
};

const LearningJourneyRoadmap = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);
  const [informationStep, setInformationStep] = useState(null);
  const [downloadingCertificate, setDownloadingCertificate] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.learningJourneys.getMyJourneyDetail(id);
      setDetail(res.data?.data || null);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to load journey');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const handleOpenStep = async (step) => {
    const status = step.progress?.status || 'locked';
    if (status === 'locked') {
      toast.warning('This step is locked. Complete previous steps first.');
      return;
    }

    try {
      setActing(true);
      if (status === 'unlocked') {
        await api.learningJourneys.startStep(id, step.id);
      }

      // Navigate into existing learning assets where applicable
      if (step.step_type === 'course') {
        const courseId = step.courseModules?.[0]?.course_id || step.reference_id;
        if (courseId) {
          navigate(generateModulesUrl(courseId, 'user'), {
            state: {
              journeyContext: { journeyId: Number(id), stepId: step.id },
              launchModuleId: step.courseModules?.[0]?.module_id || null
            }
          });
          return;
        }
      }
      if (step.step_type === 'assessment') {
        const assessmentId = step.assessmentLink?.assessment_id || step.reference_id;
        if (assessmentId) {
          navigate(`/user/assessments/${assessmentId}`, {
            state: { journeyContext: { journeyId: Number(id), stepId: step.id } }
          });
          return;
        }
      }
      if (step.step_type === 'survey') {
        const surveyId = step.surveyLink?.survey_id || step.reference_id;
        if (surveyId) {
          navigate(`/user/surveys/${surveyId}/respond`, {
            state: { journeyContext: { journeyId: Number(id), stepId: step.id } }
          });
          return;
        }
      }
      if (step.step_type === 'vilt') {
        toast.info('Choose a VILT resource below.');
        return;
      }
      if (step.step_type === 'info') {
        setInformationStep(step);
        return;
      }
      if (step.step_type === 'certificate') {
        if (detail?.progress?.certificate_issued) {
          await handleDownloadCertificate();
          return;
        }
        toast.info('Certificate unlocks when the journey is fully completed.');
        return;
      }

      // Generic complete for steps that were opened
      await load();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Unable to open step');
    } finally {
      setActing(false);
    }
  };

  const handleOpenCourseModule = async (step, moduleId) => {
    const courseId = step.courseModules?.[0]?.course_id || step.reference_id;
    if (!courseId) return;

    if (step.progress?.status === 'unlocked') {
      await api.learningJourneys.startStep(id, step.id);
    }
    navigate(generateModulesUrl(courseId, 'user'), {
      state: {
        journeyContext: { journeyId: Number(id), stepId: step.id },
        launchModuleId: moduleId
      }
    });
  };

  const handleOpenVltResource = async (step, resource) => {
    if (!resource?.id || !resource.url_or_path) {
      toast.error('This resource is not available');
      return;
    }

    try {
      setActing(true);
      if (step.progress?.status === 'unlocked') {
        await api.learningJourneys.startStep(id, step.id);
      }
      await api.vlt.trackAccess(resource.id, 'view', {
        journey_id: Number(id),
        journey_step_id: step.id
      });
      const resourceUrl = resource.url_or_path.startsWith('http')
        ? resource.url_or_path
        : getAssetUrl(resource.url_or_path);
      window.open(resourceUrl, '_blank', 'noopener,noreferrer');
      await api.learningJourneys.completeStep(id, step.id, {
        resource_viewed: true,
        attendance_marked: true
      });
      await load();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Unable to open resource');
    } finally {
      setActing(false);
    }
  };

  const handleAcknowledgeInformation = async () => {
    if (!informationStep) return;
    try {
      setActing(true);
      await api.learningJourneys.completeStep(id, informationStep.id, { force_complete: true });
      setInformationStep(null);
      toast.success('Information viewed — next module unlocked');
      await load();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Unable to continue');
    } finally {
      setActing(false);
    }
  };

  const handleDownloadCertificate = async () => {
    const certificateId = detail?.progress?.issued_certificate_id;
    if (!certificateId) {
      navigate('/user/certificates');
      return;
    }

    try {
      setDownloadingCertificate(true);
      const response = await api.certificates.downloadCertificate(certificateId);
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `${detail?.journey?.name || 'learning-journey'}-certificate.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Certificate downloaded');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Certificate download failed');
    } finally {
      setDownloadingCertificate(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 via-white to-indigo-50">
        <Spinner animation="border" />
      </div>
    );
  }

  if (!detail) {
    return (
      <div className="p-8 text-center">
        <p>Journey not found.</p>
        <Button onClick={() => navigate('/user/learning-journeys')}>Back</Button>
      </div>
    );
  }

  const { journey, progress, roadmap } = detail;
  const percent = Math.round(progress?.progress_percent || 0);
  const completedSteps = roadmap.filter((step) => step.progress?.status === 'completed').length;
  const roadHeight = Math.max(480, (roadmap.length * 220) + 220);
  const roadPath = buildRoadPath(roadmap.length);

  return (
    <main className="min-h-screen bg-gradient-to-br from-slate-100 via-blue-50 to-indigo-100 p-3 sm:p-6 lg:p-8">
      <div className="mx-auto max-w-6xl">
        <header className="mb-5 flex items-center gap-3">
          <Button
            bsPrefix="custom-hamburger"
            className="flex h-10 w-10 flex-none items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-600 shadow-sm hover:bg-slate-50"
            onClick={() => navigate('/user/learning-journeys')}
            aria-label="Back to learning journeys"
          >
            <FaArrowLeft aria-hidden="true" />
          </Button>
          <div className="min-w-0">
            <p className="mb-1 text-xs font-bold uppercase tracking-[0.2em] text-blue-600">Learning Journey</p>
            <h1 className="mb-0 truncate text-2xl font-extrabold text-slate-900 sm:text-3xl">{journey.name}</h1>
          </div>
        </header>

        <section className="mb-6 rounded-2xl border border-slate-200 bg-white p-4 shadow-lg sm:p-5" aria-label="Journey progress">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <div>
              <p className="mb-0 font-bold text-slate-900">{percent}% Complete</p>
              <p className="mb-0 text-sm text-slate-500">{completedSteps} of {roadmap.length} modules completed</p>
            </div>
            <div className="flex flex-wrap items-center gap-2 text-sm text-slate-500">
              <span>{journey.category || 'Learning Path'}</span>
              <span aria-hidden="true">•</span>
              <span>{journey.estimated_duration || 'Self-paced'}</span>
              {progress?.certificate_issued && (
                <Badge bg="warning" text="dark" className="d-inline-flex align-items-center gap-1">
                  <FaAward aria-hidden="true" /> Certificate Available
                </Badge>
              )}
            </div>
          </div>
          <div className="h-3 overflow-hidden rounded-full bg-slate-200">
            <div
              className="h-full rounded-full bg-gradient-to-r from-blue-500 to-indigo-600 transition-all duration-700"
              style={{ width: `${percent}%` }}
              role="progressbar"
              aria-label="Journey progress"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={percent}
            />
          </div>
        </section>

        <section className="overflow-hidden rounded-3xl border border-slate-200 bg-white p-4 shadow-xl sm:p-6 lg:p-8" aria-label="Journey roadmap">
          <div className="mb-5 text-center">
            <p className="mb-1 text-xs font-bold uppercase tracking-[0.2em] text-blue-600">Your Roadmap</p>
            <h2 className="mb-1 text-2xl font-extrabold text-slate-900">Start to Finish</h2>
            <p className="mb-0 text-sm text-slate-500">Complete each module to unlock the next stop.</p>
          </div>

          {roadmap.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-slate-300 py-14 text-center text-slate-500">
              <FaRoute className="mx-auto mb-3 text-3xl text-slate-300" aria-hidden="true" />
              No modules have been added to this journey.
            </div>
          ) : (
            <div className="relative mx-auto max-w-5xl" style={{ minHeight: roadHeight }}>
              <svg
                className="pointer-events-none absolute inset-0 hidden h-full w-full lg:block"
                viewBox={`0 0 1000 ${roadHeight}`}
                preserveAspectRatio="none"
                aria-hidden="true"
              >
                <path d={roadPath} fill="none" stroke="#0f172a" strokeWidth="46" strokeLinecap="round" />
                <path d={roadPath} fill="none" stroke="#334155" strokeWidth="34" strokeLinecap="round" />
                <path d={roadPath} fill="none" stroke="#f8fafc" strokeWidth="3" strokeDasharray="12 12" strokeLinecap="round" />
              </svg>

              <div className="absolute bottom-16 left-7 top-20 w-2 rounded-full bg-slate-700 lg:hidden" aria-hidden="true">
                <div className="mx-auto h-full w-px border-l border-dashed border-white" />
              </div>

              <div className="absolute left-1 top-1 z-20 flex items-center gap-2 rounded-full border-4 border-white bg-emerald-500 px-4 py-2 font-bold text-white shadow-lg lg:left-1/2 lg:-translate-x-1/2">
                <FaMapMarkerAlt aria-hidden="true" /> Start
              </div>

              <div className="relative z-10 pt-24">
                {roadmap.map((step, index) => {
                  const status = step.progress?.status || 'locked';
                  const Icon = STEP_ICONS[step.step_type] || FaInfoCircle;
                  const locked = status === 'locked';
                  const completed = status === 'completed';
                  const active = status === 'unlocked' || status === 'in_progress';
                  const alignLeft = index % 2 === 0;
                  const courseModules = step.courseModules || [];
                  const vltResources = Array.from(new Map([
                    step.viltLink?.vlt,
                    ...(step.resources || []).map((item) => item.vltResource)
                  ].filter(Boolean).map((resource) => [resource.id, resource])).values());

                  const cardStyle = completed
                    ? 'border-emerald-300 bg-emerald-50 shadow-emerald-100'
                    : active
                      ? 'border-blue-400 bg-blue-50 shadow-[0_0_30px_rgba(59,130,246,0.35)] ring-4 ring-blue-100'
                      : 'border-slate-200 bg-slate-50 shadow-slate-100';

                  return (
                    <article
                      key={step.id}
                      className={`relative flex min-h-[220px] items-center max-lg:mb-6 max-lg:min-h-0 max-lg:justify-start max-lg:pl-16 ${
                        alignLeft ? 'justify-start' : 'justify-end'
                      }`}
                    >
                      <div className={`relative w-[44%] rounded-2xl border-2 p-4 shadow-lg transition-all max-lg:w-full sm:p-5 ${cardStyle}`}>
                        <div className="mb-3 flex items-start gap-3">
                          <span className={`relative flex h-12 w-12 flex-none items-center justify-center rounded-xl ${
                            completed
                              ? 'bg-emerald-500 text-white'
                              : active
                                ? 'animate-pulse bg-blue-600 text-white shadow-[0_0_20px_rgba(37,99,235,0.75)]'
                                : 'bg-slate-200 text-slate-500'
                          }`}>
                            {completed ? (
                              <FaCheckCircle className="text-xl" aria-hidden="true" />
                            ) : locked ? (
                              <>
                                <FaLock className="text-lg" aria-hidden="true" />
                                <FaCrown className="absolute -right-1 -top-2 text-xs text-amber-500" aria-hidden="true" />
                              </>
                            ) : (
                              <Icon className="text-xl" aria-hidden="true" />
                            )}
                          </span>
                          <div className="min-w-0 flex-grow">
                            <div className="mb-1 flex flex-wrap items-center gap-2">
                              <span className="text-xs font-bold uppercase tracking-wider text-slate-500">Module {index + 1}</span>
                              <Badge bg={completed ? 'success' : active ? 'primary' : 'secondary'}>
                                {completed ? 'Completed' : active ? 'Unlocked' : 'Locked'}
                              </Badge>
                            </div>
                            <h3 className={`mb-1 text-lg font-bold ${locked ? 'text-slate-500' : 'text-slate-900'}`}>
                              {step.title || step.step_type}
                            </h3>
                            {!['certificate', 'info'].includes(step.step_type) && (
                              <p className="mb-0 flex items-center gap-1.5 text-sm text-slate-500">
                                <FaClock aria-hidden="true" /> Estimated duration: {getStepDuration(step)}
                              </p>
                            )}
                          </div>
                        </div>

                        {step.step_type === 'course' && courseModules.length > 0 && (
                          <div className="mb-3 space-y-2">
                            {courseModules.map((courseModule, moduleIndex) => (
                              <button
                                key={courseModule.id}
                                type="button"
                                className="flex w-full items-center justify-between gap-3 rounded-xl border border-blue-200 bg-white px-3 py-2 text-left text-sm transition-colors hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-60"
                                disabled={locked || acting}
                                onClick={() => handleOpenCourseModule(step, courseModule.module_id)}
                                aria-label={`Launch ${courseModule.module?.name || `Module ${moduleIndex + 1}`}`}
                              >
                                <span className="flex min-w-0 items-center gap-2 font-semibold text-slate-700">
                                  <FaPlay className="flex-none text-blue-600" aria-hidden="true" />
                                  <span className="truncate">{courseModule.module?.name || `Module ${moduleIndex + 1}`}</span>
                                </span>
                                <span className="flex-none text-xs text-slate-500">
                                  {courseModule.module?.duration ? `${courseModule.module.duration} min` : 'SCORM'}
                                </span>
                              </button>
                            ))}
                          </div>
                        )}

                        {step.step_type === 'vilt' && vltResources.length > 0 && (
                          <div className="mb-3 space-y-2">
                            {vltResources.map((resource) => {
                              const ResourceIcon = resource.type === 'video'
                                ? FaVideo
                                : resource.type === 'document'
                                  ? FaFileAlt
                                  : FaExternalLinkAlt;
                              return (
                                <button
                                  key={resource.id}
                                  type="button"
                                  className="flex w-full items-center justify-between gap-3 rounded-xl border border-indigo-200 bg-white px-3 py-2 text-left text-sm transition-colors hover:bg-indigo-50 disabled:cursor-not-allowed disabled:opacity-60"
                                  disabled={locked || acting}
                                  onClick={() => handleOpenVltResource(step, resource)}
                                  aria-label={`Open ${resource.title}`}
                                >
                                  <span className="flex min-w-0 items-center gap-2 font-semibold text-slate-700">
                                    <ResourceIcon className="flex-none text-indigo-600" aria-hidden="true" />
                                    <span className="truncate">{resource.title}</span>
                                  </span>
                                  <FaExternalLinkAlt className="flex-none text-xs text-slate-400" aria-hidden="true" />
                                </button>
                              );
                            })}
                          </div>
                        )}

                        {active && !['course', 'vilt'].includes(step.step_type) && (
                          <div className="flex flex-wrap gap-2">
                            <Button
                              bsPrefix="custom-hamburger"
                              className="inline-flex items-center gap-2 rounded-xl border-0 bg-blue-600 px-4 py-2 text-sm font-bold text-white shadow-md hover:bg-blue-700"
                              disabled={acting}
                              onClick={() => handleOpenStep(step)}
                              aria-label={`${status === 'unlocked' ? 'Start' : 'Continue'} ${step.title || step.step_type}`}
                            >
                              <FaPlay aria-hidden="true" />
                              {step.step_type === 'survey'
                                ? 'Open Survey'
                                : step.step_type === 'assessment'
                                  ? 'Start Assessment'
                                  : step.step_type === 'info'
                                    ? 'View Information'
                                    : step.step_type === 'certificate'
                                      ? 'Download Certificate'
                                      : status === 'unlocked' ? 'Start' : 'Continue'}
                            </Button>
                          </div>
                        )}

                        {completed && !['course', 'vilt'].includes(step.step_type) && (
                          <Button
                            bsPrefix="custom-hamburger"
                            className="inline-flex items-center gap-2 rounded-xl border border-emerald-300 bg-white px-4 py-2 text-sm font-semibold text-emerald-700 hover:bg-emerald-50"
                            disabled={acting}
                            onClick={() => step.step_type === 'certificate' && progress?.certificate_issued
                              ? handleDownloadCertificate()
                              : handleOpenStep(step)}
                          >
                            {step.step_type === 'certificate' ? <FaDownload aria-hidden="true" /> : <FaPlay aria-hidden="true" />}
                            {step.step_type === 'certificate'
                              ? downloadingCertificate ? 'Downloading...' : 'Download Certificate'
                              : 'Review Module'}
                          </Button>
                        )}

                        {locked && !['certificate', 'info'].includes(step.step_type) && (
                          <p className="mb-0 text-sm font-medium text-slate-500">
                            Complete the previous module to unlock this lesson.
                          </p>
                        )}
                      </div>
                    </article>
                  );
                })}
              </div>

              <div className="absolute bottom-1 left-1 z-20 flex items-center gap-2 rounded-full border-4 border-white bg-rose-500 px-4 py-2 font-bold text-white shadow-lg lg:left-1/2 lg:-translate-x-1/2">
                <FaFlagCheckered aria-hidden="true" /> Finish
              </div>
            </div>
          )}
        </section>

        <Modal show={Boolean(informationStep)} onHide={() => !acting && setInformationStep(null)} centered size="lg">
          <Modal.Header closeButton={!acting} className="border-0 bg-gradient-to-r from-blue-600 to-indigo-700 text-white">
            <Modal.Title className="flex items-center gap-2">
              <FaInfoCircle aria-hidden="true" />
              {informationStep?.title || 'Information'}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="bg-slate-50 p-5 sm:p-6">
            <div className="whitespace-pre-wrap rounded-2xl border border-slate-200 bg-white p-4 leading-7 text-slate-700 shadow-sm">
              {informationStep?.description || 'No additional information is available.'}
            </div>
          </Modal.Body>
          <Modal.Footer className="border-0 bg-slate-100">
            <Button
              bsPrefix="custom-hamburger"
              className="rounded-xl border-0 bg-blue-600 px-5 py-2 font-semibold text-white shadow-md hover:bg-blue-700"
              disabled={acting}
              onClick={() => informationStep?.progress?.status === 'completed'
                ? setInformationStep(null)
                : handleAcknowledgeInformation()}
            >
              {acting
                ? <Spinner size="sm" animation="border" />
                : informationStep?.progress?.status === 'completed' ? 'Close' : 'Continue Journey'}
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </main>
  );
};

export default LearningJourneyRoadmap;
